// src/screens/s2/S3Bridge.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Session 2 Screen 16 — S3 Bridge.
// Closing narrative, Session 3 date input, what-to-bring checklist, and the
// session tracker tick that marks S2 complete (reuses the existing
// voyantBreak2Complete flag already consulted elsewhere in the app).
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { ClientData } from '../../types/ClientData';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

const CHECKLIST = [
  'Last 3 months bank statements (business + personal)',
  'Updated pension statement (if changed since S1)',
  'Voyant scenario sheet — if you\'ve been exploring retirement years',
  'A list of the "what-ifs" you\'ve been quietly carrying',
  'Partner if joint decision — Session 3 is the life-design conversation',
];

export default function S3Bridge({ data, onUpdate }: Props) {
  const s2Complete = !!data.voyantBreak2Complete;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-ma-navy">Session 3 bridge</h2>
        <p className="text-gray-500 mt-1 text-sm">
          You've set the business engine running. Session 3 is where we design
          the life it's funding.
        </p>
      </div>

      {/* CLOSING NARRATIVE */}
      <div
        className="rounded-2xl p-8 mb-6 text-white"
        style={{ background: 'linear-gradient(135deg, #1B2B4B 0%, #2a3f6b 100%)' }}
      >
        <p className="text-base leading-relaxed opacity-95 mb-4">
          You now have a personal plan and a business plan working together.
        </p>
        <p className="text-base leading-relaxed opacity-95 mb-4">
          Session 3 is where we zoom out — <strong>your life design</strong>.
        </p>
        <p className="text-base leading-relaxed opacity-95 mb-4">
          Earlier retirement. Phased exit. The house move.
        </p>
        <p className="text-base leading-relaxed opacity-95 mb-4">
          What does the ideal version of your future actually look like?
        </p>
        <p className="text-xl font-semibold" style={{ color: '#C9A84C' }}>
          Voyant will show us exactly what's possible.
        </p>
      </div>

      {/* SESSION 3 DATE */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
          Session 3 — book the date
        </div>
        <div className="flex items-center gap-4">
          <input
            type="date"
            value={data.session3Date || ''}
            onChange={(e) => onUpdate({ session3Date: e.target.value })}
            className="text-xl font-bold text-ma-navy bg-transparent border border-gray-300 rounded-lg px-4 py-3"
          />
          {data.session3Date && (
            <span className="text-sm text-emerald-700 font-medium">Session 3 confirmed.</span>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Typically 8–12 weeks after S2 to give the new standing orders time to
          run and the Xero numbers time to move.
        </p>
      </div>

      {/* CHECKLIST */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
        <h3 className="text-sm font-bold text-ma-navy uppercase tracking-wide mb-4">
          What to bring to Session 3
        </h3>
        <ul className="space-y-2">
          {CHECKLIST.map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <div
                className="flex-shrink-0 w-5 h-5 rounded border-2 border-gray-300 mt-0.5"
              />
              <span className="text-sm text-gray-700">{item}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* SESSION TRACKER */}
      <div
        className="rounded-2xl p-6 flex items-center justify-between"
        style={{
          backgroundColor: s2Complete ? '#ECFDF5' : '#FFFFFF',
          border: `2px solid ${s2Complete ? '#10B981' : '#E5E7EB'}`,
        }}
      >
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
            Session tracker
          </div>
          <div className="text-lg font-bold" style={{ color: s2Complete ? '#10B981' : '#1B2B4B' }}>
            {s2Complete ? 'Session 2 marked complete.' : 'Ready to close Session 2.'}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Marks voyantBreak2Complete — unlocks Session 3 scenario work.
          </p>
        </div>
        <button
          onClick={() => onUpdate({ voyantBreak2Complete: !s2Complete })}
          className="px-6 py-3 rounded-lg font-semibold text-sm transition-colors"
          style={{
            background: s2Complete ? '#FFFFFF' : '#10B981',
            color: s2Complete ? '#10B981' : '#FFFFFF',
            border: s2Complete ? '1px solid #10B981' : 'none',
          }}
        >
          {s2Complete ? 'Mark incomplete' : 'Mark S2 complete'}
        </button>
      </div>
    </div>
  );
}
